'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import {
  RefreshCw,
  Ticket,
  Clock,
  CheckCircle2,
  TrendingUp,
  Users,
  BarChart3,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from '@/lib/toast';
import {
  getStatsOverview,
  getStatusDistribution,
  getCategoryDistribution,
  getTrends,
  getWorkload,
  StatsOverview,
  DistributionItem,
  TrendItem,
  WorkloadItem,
  getStatusLabel,
  getStatusColor,
  ApiClientError,
} from '@/services/api/tickets';

/**
 * 统计分析页面
 */
export default function StatsPage() {
  const { t } = useTranslation();

  // 状态
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState<StatsOverview | null>(null);
  const [statusDist, setStatusDist] = useState<DistributionItem[]>([]);
  const [categoryDist, setCategoryDist] = useState<DistributionItem[]>([]);
  const [trends, setTrends] = useState<TrendItem[]>([]);
  const [workload, setWorkload] = useState<WorkloadItem[]>([]);

  // 加载数据
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const [overviewData, statusData, categoryData, trendsData, workloadData] = await Promise.all([
        getStatsOverview(),
        getStatusDistribution(),
        getCategoryDistribution(),
        getTrends(7),
        getWorkload(),
      ]);

      setOverview(overviewData);
      setStatusDist(statusData);
      setCategoryDist(categoryData);
      setTrends(trendsData);
      setWorkload(workloadData);
    } catch (error) {
      if (error instanceof ApiClientError) {
        toast.error(error.message);
      } else {
        toast.error(t.tickets?.error?.loadFailed || '加载失败');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" style={{ color: '#3370ff' }} />
          <p className="mt-4 text-sm" style={{ color: '#8f959e' }}>
            {'加载中...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 - 飞书风格，标准 h-12 高度 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
              <span className="text-sm font-medium" style={{ color: '#1f2329' }}>
              {t.tickets?.admin?.stats || '统计分析'}
            </span>
            <button
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: '#f7f8fa',
                color: '#1f2329'
              }}
              onMouseEnter={(e) => {
                if (!refreshing) e.currentTarget.style.backgroundColor = '#eff0f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f7f8fa';
              }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {'刷新'}
            </button>
          </div>
        </div>
      </div>

      {/* 内容区域 - flex-1 自动占满剩余空间 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {'统计分析'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              工单数据统计和趋势分析
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>
      </div>

      {/* 统计概览 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">全部工单</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{overview?.total?.all ?? 0}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <Ticket className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待处理</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{overview?.total?.open ?? 0}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">SLA 达成率</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {((overview?.sla?.rate ?? 0) * 100).toFixed(1)}%
              </p>
            </div>
            <div className={`rounded-lg p-3 ${(overview?.sla?.rate ?? 0) >= 0.9 ? 'bg-green-50' : 'bg-orange-50'}`}>
              {(overview?.sla?.rate ?? 0) >= 0.9 ? (
                <TrendingUp className="w-6 h-6 text-green-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">满意度</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">
                {overview?.satisfaction?.average ? `${overview.satisfaction.average.toFixed(1)}/5` : '-'}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <CheckCircle2 className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* 状态分布 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">状态分布</h2>
          {statusDist.length === 0 ? (
            <p className="text-gray-500 text-sm">暂无数据</p>
          ) : (
            <div className="space-y-3">
              {statusDist.map((item, index) => {
                const color = item.status ? getStatusColor(item.status) : { bg: 'bg-gray-100', text: 'text-gray-600' };
                return (
                  <div key={index} className="flex items-center">
                    <div className="w-24 text-sm text-gray-600">
                      {item.status ? getStatusLabel(item.status, t) : (t.common?.unknown || '未知')}
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${color.bg.replace('50', '400')}`}
                          style={{ width: `${item.percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="w-16 text-right text-sm font-medium text-gray-900">
                      {item.count} ({item.percentage.toFixed(1)}%)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 分类分布 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">分类分布</h2>
          {categoryDist.length === 0 ? (
            <p className="text-gray-500 text-sm">暂无数据</p>
          ) : (
            <div className="space-y-3">
              {categoryDist.slice(0, 5).map((item, index) => (
                <div key={index} className="flex items-center">
                  <div className="w-32 text-sm text-gray-600 truncate">
                    {item.category?.name || (t.common?.uncategorized || '未分类')}
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-400"
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm font-medium text-gray-900">
                    {item.count} ({item.percentage.toFixed(1)}%)
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 趋势图 */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">近7天趋势</h2>
        {trends.length === 0 ? (
          <p className="text-gray-500 text-sm">暂无数据</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">日期</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">新建</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">解决</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">关闭</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {trends.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-600">{item.date}</td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-sm font-medium text-blue-600">{item.created}</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-sm font-medium text-green-600">{item.resolved}</span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className="text-sm font-medium text-gray-600">{item.closed}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 工作量 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">处理人工作量</h2>
        {workload.length === 0 ? (
          <p className="text-gray-500 text-sm">暂无数据</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">处理人</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">总计</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">待处理</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">处理中</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">已解决</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">平均解决时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {workload.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <Users className="w-4 h-4 text-gray-500" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {item.assignee?.displayName || (t.common?.unknown || '未知')}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-center text-sm font-medium text-gray-900">{item.total}</td>
                    <td className="px-4 py-2 text-center text-sm text-yellow-600">{item.open}</td>
                    <td className="px-4 py-2 text-center text-sm text-blue-600">{item.inProgress}</td>
                    <td className="px-4 py-2 text-center text-sm text-green-600">{item.resolved}</td>
                    <td className="px-4 py-2 text-center text-sm text-gray-600">
                      {item.avgResolutionTime ? `${(item.avgResolutionTime / 60).toFixed(1)}小时` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
